import { useEffect, useState } from 'react';
import {
  App,
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Steps,
  Typography,
} from 'antd';
import { GoogleOutlined, EditOutlined } from '@ant-design/icons';
import {
  useCreateEnterprise,
  useUpdateEnterprise,
  useGenerateSignupUrl,
  useCompleteEnterpriseSignup,
} from '@/hooks/useEnterprises';
import { useAccessibleMdms } from '@/hooks/useMdms';
import type {
  CreateEnterpriseRequest,
  EnterpriseDto,
  UpdateEnterpriseRequest,
  SignupUrlDto,
} from '@/types/enterprise';

const { Text, Paragraph } = Typography;

type CreationMode = 'select' | 'google' | 'manual';

// Estado del signup flow
type SignupStep = 'generating' | 'waiting' | 'completing' | 'done';

interface Props {
  open: boolean;
  enterprise: EnterpriseDto | null;
  onClose: () => void;
}

const ENTERPRISE_TYPES = [
  { value: 'MANAGED_GOOGLE_PLAY_ACCOUNTS_ENTERPRISE', label: 'Managed Google Play Accounts' },
  { value: 'MANAGED_GOOGLE_DOMAIN_ENTERPRISE', label: 'Managed Google Domain' },
];

const MANAGEMENT_MODES = [
  { value: 'CUSTOMER_MANAGED', label: 'Customer Managed' },
  { value: 'EMM_MANAGED', label: 'EMM Managed' },
];

export function EnterpriseFormModal({ open, enterprise, onClose }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [completeForm] = Form.useForm();

  const isEditing = enterprise != null;

  // Mode selector (solo en creacion)
  const [mode, setMode] = useState<CreationMode>('select');

  // Estado del signup flow
  const [signupStep, setSignupStep] = useState<SignupStep>('generating');
  const [signupData, setSignupData] = useState<SignupUrlDto | null>(null);

  const createMutation = useCreateEnterprise();
  const updateMutation = useUpdateEnterprise();
  const generateSignupUrlMutation = useGenerateSignupUrl();
  const completeSignupMutation = useCompleteEnterpriseSignup();

  const { data: accessibleMdms } = useAccessibleMdms();

  const loading =
    createMutation.isPending ||
    updateMutation.isPending ||
    generateSignupUrlMutation.isPending ||
    completeSignupMutation.isPending;

  // Reset al abrir/cerrar
  useEffect(() => {
    if (open) {
      if (isEditing && enterprise) {
        setMode('manual'); // edicion siempre va directo al form
        form.setFieldsValue({
          mdmId: enterprise.mdmId,
          googleEnterpriseId: enterprise.googleEnterpriseId,
          displayName: enterprise.displayName,
          displayNameEs: enterprise.displayNameEs,
          contactName: enterprise.contactName,
          contactEmail: enterprise.contactEmail,
          contactPhone: enterprise.contactPhone,
          enterpriseType: enterprise.enterpriseType,
          managementMode: enterprise.managementMode,
          googleProjectId: enterprise.googleProjectId,
          googleCredentialsPath: enterprise.googleCredentialsPath,
        });
      } else {
        setMode('select');
        setSignupStep('generating');
        setSignupData(null);
        form.resetFields();
        form.setFieldsValue({
          enterpriseType: 'MANAGED_GOOGLE_PLAY_ACCOUNTS_ENTERPRISE',
          managementMode: 'CUSTOMER_MANAGED',
        });
        completeForm.resetFields();
      }
    }
  }, [open, enterprise, isEditing, form, completeForm]);

  // ============================================================
  // MODO GOOGLE — Paso 1: generar URL
  // ============================================================
  async function handleStartGoogleFlow() {
    try {
      const mdmId = form.getFieldValue('mdmId');
      if (!mdmId) {
        message.error('Seleccioná el MDM padre primero.');
        return;
      }

      setSignupStep('generating');
      const data = await generateSignupUrlMutation.mutateAsync({ mdmId });
      setSignupData(data);

      // Abrir la URL de Google en una nueva pestaña
      window.open(data.signupUrl, '_blank', 'noopener,noreferrer');

      setSignupStep('waiting');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Error al generar la URL de Google.');
    }
  }

  // ============================================================
  // MODO GOOGLE — Paso 2: completar con enterpriseToken
  // ============================================================
  async function handleCompleteGoogleFlow() {
    try {
      const values = await completeForm.validateFields();

      if (!signupData) return;

      const result = await completeSignupMutation.mutateAsync({
        signupToken: signupData.signupToken,
        enterpriseToken: values.enterpriseToken.trim(),
        displayNameEs: values.displayNameEs.trim(),
        displayName: values.displayName.trim(),
        contactName: values.contactName || undefined,
        contactEmail: values.contactEmail || undefined,
        contactPhone: values.contactPhone || undefined,
      });

      message.success(`Enterprise "${result.displayNameEs}" creada. Google ID: ${result.googleEnterpriseId}`);
      onClose();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Error al crear el Enterprise.');
    }
  }

  // ============================================================
  // MODO MANUAL — crear/editar
  // ============================================================
  async function handleManualSubmit() {
    try {
      const values = await form.validateFields();

      if (isEditing && enterprise) {
        const request: UpdateEnterpriseRequest = {
          displayName: values.displayName,
          displayNameEs: values.displayNameEs,
          contactName: values.contactName || undefined,
          contactEmail: values.contactEmail || undefined,
          contactPhone: values.contactPhone || undefined,
          enterpriseType: values.enterpriseType || undefined,
          managementMode: values.managementMode || undefined,
          googleProjectId: values.googleProjectId || undefined,
          googleCredentialsPath: values.googleCredentialsPath || undefined,
        };
        await updateMutation.mutateAsync({ id: enterprise.id, request });
        message.success('Enterprise actualizada.');
      } else {
        const request: CreateEnterpriseRequest = {
          mdmId: values.mdmId,
          googleEnterpriseId: values.googleEnterpriseId,
          displayName: values.displayName,
          displayNameEs: values.displayNameEs,
          contactName: values.contactName || undefined,
          contactEmail: values.contactEmail || undefined,
          contactPhone: values.contactPhone || undefined,
          enterpriseType: values.enterpriseType || undefined,
          managementMode: values.managementMode || undefined,
          googleProjectId: values.googleProjectId || undefined,
          googleCredentialsPath: values.googleCredentialsPath || undefined,
        };
        await createMutation.mutateAsync(request);
        message.success('Enterprise creada.');
      }

      onClose();
    } catch (err) {
      if (err instanceof Error && err.message) {
        message.error(err.message);
      }
    }
  }

  // ============================================================
  // FOOTER DINAMICO
  // ============================================================
  function renderFooter() {
    if (isEditing || mode === 'manual') {
      return [
        <Button key="cancel" onClick={onClose}>
          Cancelar
        </Button>,
        <Button key="ok" type="primary" loading={loading} onClick={handleManualSubmit}>
          {isEditing ? 'Guardar' : 'Crear'}
        </Button>,
      ];
    }

    if (mode === 'google') {
      if (signupStep === 'waiting') {
        return [
          <Button key="cancel" onClick={onClose}>
            Cancelar
          </Button>,
          <Button key="reopen" onClick={() => window.open(signupData?.signupUrl, '_blank', 'noopener,noreferrer')}>
            Abrir URL de nuevo
          </Button>,
          <Button key="ok" type="primary" loading={loading} onClick={handleCompleteGoogleFlow}>
            Completé el wizard →
          </Button>,
        ];
      }
      return [
        <Button key="cancel" onClick={onClose}>
          Cancelar
        </Button>,
      ];
    }

    // mode === 'select'
    return [
      <Button key="cancel" onClick={onClose}>
        Cancelar
      </Button>,
    ];
  }

  // ============================================================
  // CONTENIDO DEL MODAL
  // ============================================================

  // Selector de MDM (compartido entre modos para creacion)
  function renderMdmSelector() {
    return (
      <Form.Item
        name="mdmId"
        label="MDM padre"
        rules={[{ required: true, message: 'Requerido' }]}
        extra={
          <Text type="secondary" style={{ fontSize: 12 }}>
            MDM al que pertenece este Enterprise.
          </Text>
        }
      >
        <Select
          placeholder="Seleccionar MDM padre..."
          options={(accessibleMdms ?? []).map((m) => ({
            value: m.id,
            label: `${m.name} (${m.code})`,
          }))}
        />
      </Form.Item>
    );
  }

  function renderContent() {
    // EDICION — siempre el form completo
    if (isEditing) {
      return (
        <Form form={form} layout="vertical" requiredMark="optional">
          <Form.Item name="mdmId" label="MDM padre" extra={
            <Text type="warning" style={{ fontSize: 12 }}>
              No se puede mover un Enterprise entre MDMs desde aqui.
            </Text>
          }>
            <Select disabled options={(accessibleMdms ?? []).map((m) => ({ value: m.id, label: `${m.name} (${m.code})` }))} />
          </Form.Item>

          <Form.Item name="googleEnterpriseId" label="Google Enterprise ID" extra={
            <Text type="warning" style={{ fontSize: 12 }}>No editable. Es la referencia inmutable a Google.</Text>
          }>
            <Input disabled />
          </Form.Item>

          <Form.Item name="displayNameEs" label="Nombre (en español, para UI)"
            rules={[{ required: true, message: 'Requerido' }, { max: 200 }]}>
            <Input />
          </Form.Item>

          <Form.Item name="displayName" label="Nombre (interno / inglés)"
            rules={[{ required: true, message: 'Requerido' }, { max: 200 }]}>
            <Input />
          </Form.Item>

          <Form.Item name="contactName" label="Contacto - Nombre" rules={[{ max: 200 }]}>
            <Input />
          </Form.Item>

          <Form.Item name="contactEmail" label="Contacto - Email"
            rules={[{ type: 'email', message: 'Email invalido' }, { max: 256 }]}>
            <Input />
          </Form.Item>

          <Form.Item name="contactPhone" label="Contacto - Telefono" rules={[{ max: 50 }]}>
            <Input />
          </Form.Item>

          <Form.Item name="enterpriseType" label="Tipo de Enterprise">
            <Select options={ENTERPRISE_TYPES} allowClear />
          </Form.Item>

          <Form.Item name="managementMode" label="Modo de gestion">
            <Select options={MANAGEMENT_MODES} allowClear />
          </Form.Item>
        </Form>
      );
    }

    // SELECCION DE MODO (creacion)
    if (mode === 'select') {
      return (
        <Form form={form} layout="vertical" requiredMark="optional">
          {renderMdmSelector()}

          <div style={{ marginTop: 24 }}>
            <Text strong>¿Cómo querés crear el Enterprise?</Text>
            <Space direction="vertical" style={{ width: '100%', marginTop: 12 }} size={12}>
              <Button
                block
                size="large"
                icon={<GoogleOutlined />}
                onClick={() => setMode('google')}
                style={{ height: 64, textAlign: 'left' }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>Crear via Google (recomendado)</div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    Abrís el wizard oficial de Google y el sistema lo registra automáticamente
                  </div>
                </div>
              </Button>

              <Button
                block
                size="large"
                icon={<EditOutlined />}
                onClick={() => setMode('manual')}
                style={{ height: 64, textAlign: 'left' }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>Ingreso manual</div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    Ya tenés el Google Enterprise ID y lo cargás directamente
                  </div>
                </div>
              </Button>
            </Space>
          </div>
        </Form>
      );
    }

    // MODO MANUAL — creacion
    if (mode === 'manual') {
      return (
        <Form form={form} layout="vertical" requiredMark="optional">
          {renderMdmSelector()}

          <Form.Item
            name="googleEnterpriseId"
            label="Google Enterprise ID"
            rules={[{ required: true, message: 'Requerido' }, { max: 64 }]}
            extra={<Text type="secondary" style={{ fontSize: 12 }}>Ej: LC038iy0h0 (sin el prefijo enterprises/)</Text>}
          >
            <Input placeholder="LC038iy0h0" />
          </Form.Item>

          <Form.Item name="displayNameEs" label="Nombre (en español, para UI)"
            rules={[{ required: true, message: 'Requerido' }, { max: 200 }]}>
            <Input />
          </Form.Item>

          <Form.Item name="displayName" label="Nombre (interno / inglés)"
            rules={[{ required: true, message: 'Requerido' }, { max: 200 }]}>
            <Input />
          </Form.Item>

          <Form.Item name="contactName" label="Contacto - Nombre" rules={[{ max: 200 }]}>
            <Input />
          </Form.Item>

          <Form.Item name="contactEmail" label="Contacto - Email"
            rules={[{ type: 'email', message: 'Email invalido' }, { max: 256 }]}>
            <Input />
          </Form.Item>

          <Form.Item name="contactPhone" label="Contacto - Teléfono" rules={[{ max: 50 }]}>
            <Input />
          </Form.Item>

          <Form.Item name="enterpriseType" label="Tipo de Enterprise">
            <Select options={ENTERPRISE_TYPES} allowClear />
          </Form.Item>

          <Form.Item name="managementMode" label="Modo de gestión">
            <Select options={MANAGEMENT_MODES} allowClear />
          </Form.Item>
        </Form>
      );
    }

    // MODO GOOGLE — Paso 1: generar y abrir URL
    if (mode === 'google' && signupStep === 'generating') {
      return (
        <Form form={form} layout="vertical" requiredMark="optional">
          {renderMdmSelector()}

          <Alert
            style={{ marginTop: 16 }}
            type="info"
            showIcon
            message="Wizard de Google"
            description={
              <>
                Al hacer click en "Abrir wizard de Google" se va a abrir una nueva pestaña con el
                wizard oficial de Managed Google Play. Completalo logueado con la cuenta admin del MDM
                (ej: mdmadmin@navius.com.ar). Al terminar, Google te va a redirigir a una URL que
                contiene el <b>enterpriseToken</b> — necesitás copiarlo para el paso siguiente.
              </>
            }
          />

          <Button
            block
            type="primary"
            size="large"
            icon={<GoogleOutlined />}
            loading={generateSignupUrlMutation.isPending}
            onClick={handleStartGoogleFlow}
            style={{ marginTop: 16 }}
          >
            Abrir wizard de Google
          </Button>
        </Form>
      );
    }

    // MODO GOOGLE — Paso 2: completar con enterpriseToken
    if (mode === 'google' && signupStep === 'waiting') {
      return (
        <>
          <Steps
            size="small"
            current={1}
            style={{ marginBottom: 24 }}
            items={[
              { title: 'Wizard abierto' },
              { title: 'Completar datos' },
              { title: 'Enterprise creada' },
            ]}
          />

          <Alert
            type="success"
            showIcon
            message="Wizard abierto en nueva pestaña"
            description={
              <>
                <Paragraph style={{ marginBottom: 8 }}>
                  Completá el wizard de Google. Al terminar, Google va a redirigir a una URL del tipo:
                </Paragraph>
                <Text code style={{ fontSize: 11, wordBreak: 'break-all' }}>
                  https://localhost/mdm-signup-callback?signupToken=...&enterpriseToken=<b>AQUI_ESTA</b>
                </Text>
                <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                  La página va a dar error (es normal). Copiá el valor del parámetro <b>enterpriseToken</b> de la barra de direcciones y pegalo abajo.
                </Paragraph>
              </>
            }
          />

          <Form form={completeForm} layout="vertical" requiredMark="optional" style={{ marginTop: 20 }}>
            <Form.Item
              name="enterpriseToken"
              label="Enterprise Token (del callback URL)"
              rules={[{ required: true, message: 'Requerido' }]}
            >
              <Input.TextArea
                rows={2}
                placeholder="EADhVzuyWvWj1GCXmct..."
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
            </Form.Item>

            <Form.Item
              name="displayNameEs"
              label="Nombre del cliente (para la UI)"
              rules={[{ required: true, message: 'Requerido' }, { max: 200 }]}
            >
              <Input placeholder="Global Connect S.A." />
            </Form.Item>

            <Form.Item
              name="displayName"
              label="Nombre en Google (inglés)"
              rules={[{ required: true, message: 'Requerido' }, { max: 200 }]}
              extra={<Text type="secondary" style={{ fontSize: 12 }}>Si es igual al nombre en español, repetilo.</Text>}
            >
              <Input placeholder="Global Connect" />
            </Form.Item>

            <Form.Item name="contactEmail" label="Email de contacto"
              rules={[{ type: 'email', message: 'Email invalido' }, { max: 256 }]}>
              <Input placeholder="contacto@empresa.com" />
            </Form.Item>

            <Form.Item name="contactName" label="Nombre de contacto" rules={[{ max: 200 }]}>
              <Input placeholder="Oscar Di Criscenzo" />
            </Form.Item>

            <Form.Item name="contactPhone" label="Teléfono" rules={[{ max: 50 }]}>
              <Input placeholder="+54 11 1234-5678" />
            </Form.Item>
          </Form>
        </>
      );
    }

    return null;
  }

  return (
    <Modal
      open={open}
      title={
        isEditing
          ? `Editar Enterprise: ${enterprise?.displayNameEs}`
          : 'Nueva Enterprise'
      }
      onCancel={onClose}
      footer={renderFooter()}
      confirmLoading={loading}
      width={640}
      destroyOnHidden
    >
      {renderContent()}
    </Modal>
  );
}
